
import { useState, useEffect, useRef } from "react";
import Modal from "react-bootstrap/Modal";
import { Scanner } from '@yudiel/react-qr-scanner';
import ClientsCode from "./ClientsCode";
import LeaveConfirmModal from "./LeaveConfirmModal";
import "../../../styles/vipcode.css";
import "../../../styles/checkboxCustom.css";
import { isAlphanumeric } from "../../../js/helper";
import { InfoIcon } from "../../../image/icons";

// DOMException names raised when the camera can't be opened.
const CAMERA_ERROR_MESSAGES = {
    NotAllowedError: "Camera access was denied. Please allow camera permission and try again.",
    NotFoundError: "No camera was found on this device.",
    NotReadableError: "The camera is currently in use by another app. Close it and try again.",
    OverconstrainedError: "The camera could not be started with the required settings.",
    SecurityError: "Camera access is blocked. Please check your browser settings.",
};

export const ScanQr = ({ showScanQr, setShowScanQr, setVipCode, merchantName }) => {
    const [isPaused, setIsPaused] = useState(false);
    const [showClientsCode, setShowClientsCode] = useState(false);
    const [clientCode, setClientCode] = useState("");
    const [showLeaveConfirm, setShowLeaveConfirm] = useState(false);

    const handleAttemptClose = () => {
        setShowLeaveConfirm(true);
    };

    const handleLeaveCancel = () => {
        setShowLeaveConfirm(false);
    };

    const handleLeaveConfirmed = () => {
        setShowLeaveConfirm(false);
        setShowScanQr(false);
    };
    // Camera-open failure message shown in place of the scanner; the manual-code
    // fallback ("Provide code instead") stays available underneath.
    const [cameraError, setCameraError] = useState("");
    // Available video input devices and the one currently selected ("" = auto / rear).
    const [devices, setDevices] = useState([]);
    const [selectedDeviceId, setSelectedDeviceId] = useState("");
    // The scanner can emit the same failure repeatedly — only surface it once per open.
    const cameraErrorReportedRef = useRef(false);

    useEffect(() => {
        if (showScanQr) {
            cameraErrorReportedRef.current = false;
            setCameraError("");
            setSelectedDeviceId("");
        }
    }, [showScanQr]);

    // Enumerate available cameras while the scanner is open (labels populate once permission is granted).
    useEffect(() => {
        if (!showScanQr || !navigator.mediaDevices?.enumerateDevices) return;

        let cancelled = false;
        const loadDevices = () => {
            navigator.mediaDevices.enumerateDevices()
                .then((all) => {
                    if (cancelled) return;
                    const cams = all.filter((d) => d.kind === "videoinput");
                    setDevices(cams);
                    // Ensure exactly one camera is always selected (default to the rear camera).
                    setSelectedDeviceId((prev) => {
                        if (prev) return prev;
                        const rear = cams.find((d) => /back|rear|environment/i.test(d.label));
                        return (rear || cams[0])?.deviceId || "";
                    });
                })
                .catch((err) => console.error(err));
        };

        loadDevices();
        navigator.mediaDevices.addEventListener?.("devicechange", loadDevices);
        return () => {
            cancelled = true;
            navigator.mediaDevices.removeEventListener?.("devicechange", loadDevices);
        };
    }, [showScanQr]);

    const handleDeviceChange = (deviceId) => {
        // Switching device clears any prior camera error and lets it retry.
        cameraErrorReportedRef.current = false;
        setCameraError("");
        setSelectedDeviceId(deviceId);
    };

    const handleScannerError = (error) => {
        console.error(error);
        const domName = error?.cause?.name || error?.name || "";
        const isCameraFailure =
            error?.kind === "aborted" ||
            /videoinput|camera|getusermedia/i.test(error?.message || "") ||
            domName in CAMERA_ERROR_MESSAGES ||
            domName === "AbortError";

        if (!isCameraFailure || cameraErrorReportedRef.current) return;
        cameraErrorReportedRef.current = true;

        const message = CAMERA_ERROR_MESSAGES[domName] ||
            "Unable to start the camera. Please check camera permissions and try again.";
        setCameraError(message);
    };

    const handleRetryCamera = () => {
        cameraErrorReportedRef.current = false;
        setCameraError("");
    };

    const handleScan = (detectedCodes) => {
        detectedCodes.forEach(code => {
            if (code.format === "qr_code") {
                if (isAlphanumeric(code.rawValue)) {
                    // setIsPaused(true);
                    setShowScanQr(false);
                    setVipCode(code.rawValue);
                } else {
                    console.warn('Invalid QR code: non-alphanumeric');
                }
            }
        });
    };
    
    const handleApplyClientCode = () => {
        if (clientCode.trim()) {
            if (isAlphanumeric(clientCode.trim())) {
                // setIsPaused(true);
                setVipCode(clientCode.trim());
                setClientCode("");
                setShowClientsCode(false);
                setShowScanQr(false);
            } else {
                console.warn('Invalid client code: non-alphanumeric');
            }
        }
    };

    return (
        <>
            <Modal
                show={showScanQr}
                onHide={handleAttemptClose}
                animation={false}
                keyboard={false}
                closetimeoutms={500}
                className="scan-qr-modal"
                backdropClassName="scan-qr-bd"
                backdrop="static"
                centered
            >
                <Modal.Header closeButton className="scan-qr-header">
                    <Modal.Title className="scan-qr-title">
                        Scan the QR
                        <p className="scan-qr-merchant-name">
                            <span className="scan-qr-merchant-dot">●</span>
                            {merchantName}
                        </p>
                    </Modal.Title>
                </Modal.Header>

                <Modal.Body className="scan-qr-body">
                        {devices.length > 1 && (
                            <div className="scan-qr-camera-select-wrap">
                                <div className="device-list-container">
                                    {devices.map((device, index) => {
                                        const isSelected = selectedDeviceId === device.deviceId;
                                        return (
                                            <label key={device.deviceId || index} className="device-item">
                                                <input
                                                    type="radio"
                                                    name="scan-qr-camera"
                                                    className="device-item-radio"
                                                    checked={isSelected}
                                                    onChange={() => handleDeviceChange(device.deviceId)}
                                                />
                                                <span className="device-item-text">
                                                    {device.label || `Camera ${index + 1}`}
                                                </span>
                                            </label>
                                        );
                                    })}
                                </div>
                            </div>
                        )}
                        {cameraError ? (
                            <div className="scan-qr-camera-error">
                                <div className="scan-qr-camera-error__icon">
                                    <InfoIcon width="40" height="40" />
                                </div>
                                <p className="scan-qr-camera-error__msg">{cameraError}</p>
                                <button
                                    type="button"
                                    className="scan-qr-camera-error__retry"
                                    onClick={handleRetryCamera}
                                >
                                    Try again
                                </button>
                            </div>
                        ) : (
                            <div className="scan-qr-scanner-wrap">
                                <Scanner
                                    key={selectedDeviceId || 'auto'}
                                    onScan={handleScan}
                                    onError={handleScannerError}
                                    sound={false}
                                    paused={isPaused}
                                    constraints={{
                                        ...(selectedDeviceId
                                            ? { deviceId: { exact: selectedDeviceId } }
                                            : { facingMode: 'environment' }),
                                        aspectRatio: 1,
                                        width: { ideal: 420 },
                                        height: { ideal: 280 },
                                    }}
                                    styles={{
                                        container: { width: '100%', height: '100%' },
                                        video: {},
                                    }}
                                    components={{ finder: false }}
                                />

                                {/* Custom overlay */}
                                <div className="scan-qr-overlay">
                                    {/* Corner brackets */}
                                    <div className="scan-qr-corner scan-qr-corner--tl" />
                                    <div className="scan-qr-corner scan-qr-corner--tr" />
                                    <div className="scan-qr-corner scan-qr-corner--bl" />
                                    <div className="scan-qr-corner scan-qr-corner--br" />

                                    {/* Animated scan line */}
                                    {!isPaused && <div className="scan-qr-scan-line" />}

                                    {/* Hint text */}
                                    <p className="scan-qr-hint">Make sure the QR code is within the frame</p>
                                </div>
                            </div>
                        )}

                    {/* Provide code instead */}
                    <div className="scan-qr-provide-code-wrap">
                        <div
                            className="scan-qr-provide-code"
                            onClick={() => setShowClientsCode(true)}
                        >
                            <div className="scan-qr-provide-code__left">
                                <span className="scan-qr-provide-code__label">Problems scanning QR</span>
                                <span className="scan-qr-provide-code__title">Provide code instead</span>
                                <span className="scan-qr-provide-code__subtitle">Tap to input client's code</span>
                            </div>
                            <div className="scan-qr-provide-code__icon">
                                <InfoIcon />
                            </div>
                        </div>
                    </div>
                </Modal.Body>
            </Modal>

            {/* Leave confirmation dialog */}
            <LeaveConfirmModal
                show={showLeaveConfirm}
                onCancel={handleLeaveCancel}
                onConfirm={handleLeaveConfirmed}
            />

            <ClientsCode
                showClientsCode={showClientsCode}
                handleClientsCodeClose={() => setShowClientsCode(false)}
                handleCancelFlow={() => {
                    setShowClientsCode(false);
                    setShowScanQr(false);
                }}
                clientCode={clientCode}
                handleClientCodeChange={(e) => setClientCode(e.target.value)}
                handleApplyClientCode={handleApplyClientCode}
            />
        </>
    );
};

export default ScanQr;
